import ContentContainer from '@/features/shared/ContentContainer';
import DetailContent from '@/features/shared/DetailContent';
import Image from '@/features/shared/Image';
import { MuiContainer, MuiTab, MuiTabs, MuiTypography } from '@/features/shared/material-ui';
import RichText from '@/features/shared/RichText';
import TwoColumnContent from '@/features/shared/TwoColumnContent';
import { Theme } from '@/utils/constants';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { ChangeEvent, forwardRef, memo, useState } from 'react';
import { VerticalThumbnailContentProps } from './VerticalThumbnailContent.interface';
import useStyles from './VerticalThumbnailContent.styles';

export const a11yProps = (index: any) => {
  return {
    id: `vertical-thumbnail-tab-${index}`,
    'aria-controls': `vertical-thumbnail-tabpanel-${index}`,
  };
};

interface TabPanelProps {
  children?: any;
  index?: any;
  value?: any;
  panelName?: string;
  classes?: any;
  className?: string;
}

const mapVariantToProps = ({ variant }: any) => {
  if (/^image$/.test(variant)) {
    return {
      type: 'featured',
    };
  }

  return {};
};

const mapVariantToDetailContentClassName =
  (classes: any) =>
  ({ variant }: any) => {
    if (/^rtc$/.test(variant)) {
      return classes.rtc;
    }

    if (/^link$/.test(variant)) {
      return classes.link;
    }

    return '';
  };

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, panelName, classes, className, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-${panelName}-${index}`}
      aria-labelledby={`vertical-thumbnail-tab-${index}`}
      className={clsx(classes.tabPanel, classes.tabPanelAnimated, className)}
      {...other}
    >
      {children}
    </div>
  );
};

const mapTabToPanelContent = ({
  variant,
  theme,
  classes,
  tab = {},
  isMobile,
}: {
  variant: string;
  theme: ThemeType;
  classes: any;
  tab: TabContentType;
  isMobile: boolean;
}) => {
  const { tabDetailContent } = tab;
  const { mediaContent } = tabDetailContent || {};

  if (/^content$/i.test(variant) && tabDetailContent) {
    const { superHeading, heading, detailContent } = tabDetailContent;

    return (
      <>
        {superHeading && (
          <MuiTypography variant="overline" component="h5" className={classes.superheading}>
            {superHeading}
          </MuiTypography>
        )}
        {heading && (
          <RichText
            customStyles
            theme={theme}
            className={clsx(classes.heading, 'gtm-headline')}
            content={heading}
          />
        )}
        {isMobile && (
          <DetailContent
            content={mediaContent}
            theme={theme}
            mapVariantToProps={mapVariantToProps}
          />
        )}
        <DetailContent
          content={detailContent}
          theme={theme}
          mapVariantToClassName={mapVariantToDetailContentClassName(classes)}
        />
      </>
    );
  }

  if (/^media$/i.test(variant) && mediaContent) {
    return (
      !isMobile && (
        <DetailContent content={mediaContent} theme={theme} mapVariantToProps={mapVariantToProps} />
      )
    );
  }

  return null;
};

const TabPanels = ({
  classes,
  tabs = [],
  value = 0,
  theme = Theme.light,
  variant = 'content',
  isMobile,
}: {
  classes: any;
  tabs: TabContentType[];
  value: number;
  theme: ThemeType;
  variant: string;
  isMobile: boolean;
}) => {
  return (
    <>
      {tabs.map((tab, i) => {
        const contentVariant = mapTabToPanelContent({
          theme,
          variant,
          classes,
          tab,
          isMobile,
        });

        return (
          <TabPanel
            // eslint-disable-next-line react/no-array-index-key
            key={`tabPanel_${i}`}
            value={value}
            panelName="tabpanel"
            index={i}
            classes={classes}
            className={variant}
          >
            {contentVariant}
          </TabPanel>
        );
      })}
    </>
  );
};

const CustomTabComponent = forwardRef((props: any, ref: any) => {
  const { children, title, imageClassName, ...tabProps } = props;
  const { thumbnail } = title;
  const hasThumbnail = thumbnail && thumbnail.original;

  return (
    <div {...tabProps} ref={ref}>
      {hasThumbnail && <Image image={thumbnail} className={imageClassName} type="featured" />}
    </div>
  );
});

const VerticalThumbnailContent = ({
  theme = Theme.light,
  tabs = [],
  containerProps,
  contentButtons,
}: Br.PropsWithContentButtons<VerticalThumbnailContentProps>) => {
  const classes = useStyles({});
  const [value, setValue] = useState(0);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  const rootClasses = clsx(classes.root, `${theme}-theme`);

  return (
    <ContentContainer
      {...containerProps}
      mobileTopBackgroundColor={containerProps?.topBackgroundColor}
      mobileBottomBackgroundColor={containerProps?.bottomBackgroundColor}
      topSpacing={containerProps?.topSpacing}
      bottomSpacing={containerProps?.bottomSpacing}
      data-component-variant="vertical-thumbail-content"
    >
      <MuiContainer className="medium">
        {contentButtons?.content}
        <div className={rootClasses}>
          {tabs.length > 0 && (
            <TwoColumnContent
              columnRatio="40-60"
              leftColumn={
                <>
                  {!isMobile && (
                    <TabPanels
                      variant="media"
                      theme={theme}
                      tabs={tabs}
                      value={value}
                      classes={classes}
                      isMobile={isMobile}
                    />
                  )}
                  <TabPanels
                    variant="content"
                    theme={theme}
                    tabs={tabs}
                    value={value}
                    classes={classes}
                    isMobile={isMobile}
                  />
                </>
              }
              rightColumn={
                <>
                  {isMobile && (
                    <TabPanels
                      variant="media"
                      theme={theme}
                      tabs={tabs}
                      value={value}
                      classes={classes}
                      isMobile={isMobile}
                    />
                  )}
                  <MuiTabs
                    orientation={isMobile ? 'horizontal' : 'vertical'}
                    variant="scrollable"
                    scrollButtons={isMobile ? 'on' : 'off'}
                    value={value}
                    onChange={handleChange}
                    aria-label="Horizontal Bottom MuiTabs"
                    className={classes.tabs}
                  >
                    {tabs.map(({ tabTitle }, i) => (
                      <MuiTab
                        // eslint-disable-next-line react/no-array-index-key
                        key={`tab_${i}`}
                        title={tabTitle}
                        {...a11yProps(i)}
                        component={CustomTabComponent}
                        className={clsx(classes.tab, 'gtm-tab')}
                        imageClassName={classes.tabImage}
                      />
                    ))}
                  </MuiTabs>
                </>
              }
            />
          )}
        </div>
      </MuiContainer>
    </ContentContainer>
  );
};

export default memo(VerticalThumbnailContent);
